class CartDrawerDiscountInput extends HTMLElement {
  constructor() {
    super();
    this.initializeElements();
    this.initializeEventListeners();
    this.loadInitialState();
  }

  initializeElements() {
    this.elements = {
      toggleButton: this.querySelector('.discount-input__button'),
      contentContainer: this.querySelector('.discount-input__content'),
      form: this.querySelector('.discount-input__form'),
      input: this.querySelector('.discount-input__input'),
      submitButton: this.querySelector('.discount-input__submit'),
      message: this.querySelector('.discount-input__message'),
      discountCodeTagWrapper: this.querySelector('.discount-code-display__tag-wrapper'),
      discountAmountValue: this.querySelector('.discount-code-display__amount .value'),
      errorMessage: this.querySelector('.discount-input__error-message'),
    };
  }

  initializeEventListeners() {
    if (this.elements.toggleButton && this.elements.contentContainer) {
      this.elements.toggleButton.addEventListener('click', this.toggleContent.bind(this));
    }

    if (this.elements.form) {
      this.elements.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
  }

  async loadInitialState() {
    try {
      const cart = await this.fetchCart();
      this.updateUIState(cart);
    } catch (error) {
      console.error('Error loading initial state:', error);
    }
  }

  async fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  updateUIState(cart) {
    const applicableDiscounts = this.findApplicableDiscounts(cart.discount_codes || []);

    if (applicableDiscounts.length > 0) {
      this.showDiscountsDisplay(cart.items, applicableDiscounts);
    } else {
      this.clearDiscountDisplay();
    }
  }

  findApplicableDiscounts(discountCodes) {
    return discountCodes.filter((discount) => discount.applicable);
  }

  calculateDiscountAmount(items, discountCode) {
    let discountAmount = 0;

    if (!items || !Array.isArray(items)) {
      return discountAmount;
    }

    items.forEach((item) => {
      if (!item || !item.discounts || !Array.isArray(item.discounts)) {
        return;
      }

      item.discounts.forEach((discount) => {
        if (discount && discount.title.toLowerCase() === discountCode.toLowerCase() && discount.amount) {
          discountAmount += discount.amount;
        }
      });
    });

    if (discountAmount > 0) {
      discountAmount = (discountAmount / 100).toFixed(2);
    }

    return discountAmount;
  }

  toggleContent() {
    if (!this.elements.contentContainer || !this.elements.toggleButton) return;

    this.elements.contentContainer.classList.toggle('active');
    this.elements.toggleButton.classList.toggle('open');
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const discountCode = this.elements.input.value.trim();
    if (!discountCode) return;

    this.applyDiscountCode(discountCode);
  }

  async applyDiscountCode(discountCode) {
    this.setSubmitButtonState(true);
    this.hideErrorMessage();

    try {
      const cart = await this.updateCartDiscount(discountCode);
      const isApplicable = this.isDiscountApplicable(cart.discount_codes, discountCode);

      if (isApplicable) {
        this.handleDiscountSuccess(discountCode);
        await this.refreshCart();
      } else {
        this.setSubmitButtonState(false);
        this.showErrorMessage();
      }
    } catch (error) {
      console.error('Error applying discount code:', error);
      this.handleDiscountError();
    }
  }

  async updateCartDiscount(discountCode, operation = 'add') {
    const formData = new FormData();

    if (operation === 'add') {
      const currentCodes = this.getAllCurrentDiscountCodes();
      const allCodes = [...currentCodes, discountCode];
      formData.append('discount', allCodes.join(','));
    } else if (operation === 'remove') {
      const currentCodes = this.getAllCurrentDiscountCodes();
      const remainingCodes = currentCodes.filter((code) => code !== discountCode);
      formData.append('discount', remainingCodes.join(','));
    } else if (operation === 'clear') {
      formData.append('discount', '');
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  getAllCurrentDiscountCodes() {
    const tags = this.elements.discountCodeTagWrapper?.querySelectorAll('.discount-code-display__tag') || [];
    return Array.from(tags).map((tag) => tag.querySelector('.discount-code-display__code')?.textContent || '');
  }

  isDiscountApplicable(discountCodes, discountCode) {
    return discountCodes.some(
      (discount) => discount.code.toLowerCase() === discountCode.toLowerCase() && discount.applicable
    );
  }

  handleDiscountSuccess(discountCode) {
    this.clearInput();
    this.setSubmitButtonState(false);
    this.fetchCart()
      .then((cart) => {
        const applicableDiscounts = this.findApplicableDiscounts(cart.discount_codes || []);
        this.showDiscountsDisplay(cart.items, applicableDiscounts);
      })
      .catch((error) => {
        console.error('Error fetching cart for discount display:', error);
      });
  }

  handleDiscountError() {
    this.setSubmitButtonState(false);
    this.showErrorMessage();
    console.error('Failed to apply discount code');
  }

  showErrorMessage() {
    if (this.elements.errorMessage) {
      this.elements.errorMessage.classList.remove('hidden');
    }
  }

  hideErrorMessage() {
    if (this.elements.errorMessage) {
      this.elements.errorMessage.classList.add('hidden');
    }
  }

  clearInput() {
    if (this.elements.input) {
      this.elements.input.value = '';
    }
  }

  setSubmitButtonState(disabled) {
    if (this.elements.submitButton) {
      this.elements.submitButton.disabled = disabled;
    }
  }

  showDiscountsDisplay(items, applicableDiscounts) {
    if (this.elements.discountCodeTagWrapper) {
      this.elements.discountCodeTagWrapper.innerHTML = '';
    }

    applicableDiscounts.forEach((discount) => {
      const discountAmount = this.calculateDiscountAmount(items, discount.code);
      this.insertDiscountCodeTag(discount.code, discountAmount);
    });

    if (this.elements.form) {
      this.elements.form.classList.remove('hidden');
    }
    if (this.elements.message) {
      this.elements.message.classList.remove('hidden');
    }
    this.attachRemoveButtonListener();
  }

  clearDiscountDisplay() {
    if (this.elements.message) {
      this.elements.message.classList.add('hidden');
    }
    if (this.elements.discountCodeTagWrapper) {
      this.elements.discountCodeTagWrapper.innerHTML = '';
    }
  }

  attachRemoveButtonListener() {
    const removeButtons =
      this.elements.discountCodeTagWrapper?.querySelectorAll('.discount-code-display__remove') || [];
    removeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const discountTag = event.target.closest('.discount-code-display__tag');
        const discountCode = discountTag?.querySelector('.discount-code-display__code')?.textContent;
        if (discountCode) {
          this.removeDiscountCode(discountCode);
        }
      });
    });
  }

  insertDiscountCodeTag(discountCode, discountAmount) {
    const existingTags = this.elements.discountCodeTagWrapper?.querySelectorAll('.discount-code-display__code') || [];
    const alreadyExists = Array.from(existingTags).some((tag) => tag.textContent === discountCode);

    if (!alreadyExists) {
      this.elements.discountCodeTagWrapper.innerHTML += `
        <div class="discount-code-display__tag">
          <div class="discount-code-display__code-wrapper">${window.cartStrings.code}<span class="discount-code-display__code">${discountCode}</span></div>
          <div class="discount-code-display__amount-wrapper">
            <span class="discount-code-display__amount">-${discountAmount} ${window.cartStrings.symbol}</span>
            <button type="button" class="discount-code-display__remove" aria-label="Remove discount code">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.1531 5.63721V14.0444C14.1531 15.0322 13.3527 15.8325 12.365 15.8325H7.63452C6.6468 15.8325 5.84644 15.0322 5.84644 14.0444V5.63721H14.1531Z" stroke="currentColor" stroke-width="0.833333" stroke-linecap="round"/>
                <path d="M8.53003 7.79443V13.6748" stroke="currentColor" stroke-linecap="round"/>
                <path d="M11.47 7.79443V13.6748" stroke="currentColor" stroke-linecap="round"/>
                <rect x="4.11987" y="5.22021" width="11.7607" height="1" rx="0.5" fill="currentColor"/>
                <rect x="7.05981" y="3.75" width="5.88033" height="0.833333" rx="0.416667" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }
  }

  async removeDiscountCode(discountCode) {
    try {
      await this.updateCartDiscount(discountCode, 'remove');
      const tags = this.elements.discountCodeTagWrapper?.querySelectorAll('.discount-code-display__tag') || [];
      tags.forEach((tag) => {
        const codeElement = tag.querySelector('.discount-code-display__code');
        if (codeElement?.textContent === discountCode) {
          tag.remove();
        }
      });

      this.updateDiscountAmountAfterRemoval();
      this.setSubmitButtonState(false);
      await this.refreshCart();
    } catch (error) {
      console.error('Error removing discount code:', error);
      this.handleDiscountError();
    }
  }

  updateDiscountAmountAfterRemoval() {
    const remainingTags = this.elements.discountCodeTagWrapper?.querySelectorAll('.discount-code-display__tag') || [];

    if (remainingTags.length === 0) {
      if (this.elements.message) {
        this.elements.message.classList.add('hidden');
      }
    } else {
      this.fetchCart()
        .then((cart) => {
          const remainingCodes = Array.from(remainingTags).map(
            (tag) => tag.querySelector('.discount-code-display__code')?.textContent || ''
          );
          const applicableDiscounts = cart.discount_codes.filter(
            (discount) => discount.applicable && remainingCodes.includes(discount.code)
          );
          this.showDiscountsDisplay(cart.items, applicableDiscounts);
        })
        .catch((error) => {
          console.error('Error updating discount amount after removal:', error);
        });
    }
  }

  async clearAllDiscountCodes() {
    try {
      await this.updateCartDiscount('', 'clear');
      this.clearDiscountDisplay();
      this.setSubmitButtonState(false);
      await this.refreshCart();
    } catch (error) {
      console.error('Error removing discount code:', error);
      this.handleDiscountError();
    }
  }

  async refreshCart() {
    try {
      const cart = await this.fetchCart();
      const eventName = window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update';
      publish(eventName, {
        source: 'discount-input',
        cartData: cart,
      });
    } catch (error) {
      console.error('Error fetching cart for refresh:', error);
      const eventName = window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update';
      publish(eventName, {
        source: 'discount-input',
        cartData: null,
      });
    }
  }
}

customElements.define('cart-drawer-discount-input', CartDrawerDiscountInput);